import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { api } from '../api/client'
import type { CodeProjectDetail, RenovateRunner, RenovateTriggerPlan, SupplyChangeOptions, SupplyChangePlan, SupplyChangeRow, SupplyPipelinePlan } from '../api/types'
import { t } from '../i18n/lingua'
import { ErrorBanner, Spinner } from './common'
import { DataTable } from './DataTable'
import { BadgePipeline, LinkEsterno, quando } from './SoftwareProdotto'

/**
 * What CENSO writes in GitLab, from the console: always two steps — the
 * options, then the **plan** the Control Plane reads (what will run or stop,
 * the diff of the CI file, GitLab's CI Lint, the consequences) with
 * «Niente è ancora stato scritto» — and only then the write. See
 * gitlab_change_service.
 */

export function conseguenza(c: string, p: { ref?: string; target?: string } = {}): string {
  return ({
    run_on_ref: t('Parte una pipeline su «{ref}» con il .gitlab-ci.yml di quel ramo: tutti i job che le sue regole prevedono, sui vostri runner.', { ref: p.ref ?? '' }),
    retry_failed_jobs: t('Ripartono i job falliti o annullati di questa pipeline:'),
    cancel_running_jobs: t('Si fermano i job in corso o in attesa di questa pipeline:'),
    branch_pipeline_runs: t('Il ramo nuovo fa partire la sua pipeline sui vostri runner, con le variabili non protette, prima di qualunque revisione.'),
    mr_to_review: t('La modifica arriva in una merge request verso «{target}»: sul ramo principale non cambia niente finché non viene approvata.', { target: p.target ?? '' }),
    auto_devops_off: t('Il progetto usa Auto DevOps e non ha un .gitlab-ci.yml: crearlo spegne Auto DevOps.'),
    merge_not_blocked: t('«Pipelines must succeed» è spento: un job fallito non blocca il merge.'),
    install_deps: t('cdxgen installerà le dipendenze per risolverle: nella pipeline servono le credenziali dei vostri registri privati, altrimenti il job fallisce o l’SBOM è parziale.'),
    gitlab_report: t('L’SBOM sarà anche un report «cyclonedx» di GitLab: su Ultimate cambiano la Dependency List e le politiche di sicurezza del progetto.'),
    workflow_rules: t('La pipeline ha «workflow:rules»: il job di CENSO gira solo nelle pipeline che quelle regole creano.'),
    renovate_token_manual: t('Il runner ha bisogno della variabile CI RENOVATE_TOKEN (token del bot con scope api, read_user, write_repository): CENSO non crea token, la aggiunge un Maintainer.'),
    renovate_runs_as_bot: t('Renovate lavora con l’account di quel token: le sue merge request portano il suo nome.'),
    renovate_schedule_manual: t('Il giro pianificato parte solo con una pianificazione di GitLab sul progetto del runner (CI/CD → Pianificazioni): la crei tu, con l’orario che preferisci.'),
    renovate_needs_runner: t('Renovate apre merge request solo quando il suo runner tratta questo progetto: dopo il merge, «Avvia Renovate adesso» o il giro pianificato del runner.'),
    security_only: t('Solo aggiornamenti di sicurezza: le altre proposte di aggiornamento sono spente.'),
    renovate_direct_only: t('Gli avvisi OSV di Renovate coprono le dipendenze dirette (npm, Maven, PyPI, Go, NuGet, Cargo, Packagist, RubyGems, Hex, Hackage): una libreria transitiva si corregge aggiornando quella che la porta.'),
    all_updates: t('Tutti gli aggiornamenti: Renovate proporrà anche quelli che non correggono vulnerabilità.'),
    format_rewritten: t('Il file viene riscritto con il rientro di due spazi: la differenza mostra anche le righe solo riformattate.'),
    gate_runs_after_sbom: t('Il job censo-gate parte dopo censo-sbom e chiede a CENSO il verdetto sull’SBOM di quella pipeline: esce con 0 se passa, 3 per un avviso (job arancione, la pipeline prosegue), 1 se blocca.'),
    gate_adds_sbom_job: t('Nella pipeline non c’è il job censo-sbom: la stessa merge request lo aggiunge, perché il cancello legge l’SBOM che produce.'),
    gate_reach_censo: t('I runner devono raggiungere CENSO all’indirizzo del cancello (HTTPS, porta 443): se non lo raggiungono, decide la regola «se CENSO non può decidere».'),
    gate_forks_skip: t('Le pipeline delle fork non hanno la variabile CENSO_GATE_TOKEN: lì il job del cancello non parte.'),
    gate_warn_mode: t('Modo «avvisa»: quello che bloccherebbe diventa un job arancione, la pipeline prosegue.'),
    gate_block_mode: t('Modo «blocca»: il job fallisce e, con «Pipelines must succeed», il merge si ferma finché non si corregge o non si scrive una valutazione VEX.'),
    gate_token_missing: t('Il token del cancello non è ancora nella variabile CI: senza, il job non parte. Attiva il cancello o rigenera il token.'),
    gate_token_written: t('CENSO crea il token e lo scrive come variabile CI mascherata (e nascosta dove GitLab lo consente) CENSO_GATE_TOKEN del progetto: serve il ruolo Maintainer al token di scrittura.'),
    gate_token_shown_once: t('La scrittura non è attiva su questa connessione: CENSO crea il token e lo mostra una volta sola, da copiare tu nella variabile CI CENSO_GATE_TOKEN (mascherata, non protetta).'),
    gate_unprotected_variable: t('La variabile non è protetta, così vale anche per i rami e le merge request: chi può far girare una pipeline può leggerla, ma con quel token ottiene solo i verdetti di questo progetto.'),
    gate_needs_job: t('Dopo, aggiungi il job del cancello alla pipeline con «Aggiungi il job del cancello»: fino ad allora nessuna pipeline lo chiama.'),
  } as Record<string, string>)[c] ?? c
}

/** A unified diff, coloured and readable without colour (+/− in the line). */
export function Diff({ testo }: { testo: string }) {
  return (
    <pre className="diff">
      {testo.split('\n').map((riga, i) => (
        <div key={i} className={riga.startsWith('+++') || riga.startsWith('---') ? 'diff__file'
          : riga.startsWith('@@') ? 'diff__hunk' : riga.startsWith('+') ? 'diff__add' : riga.startsWith('-') ? 'diff__del' : undefined}>
          {riga || ' '}
        </div>
      ))}
    </pre>
  )
}

export function Finestra({ titolo, sotto, onChiudi, children }: {
  titolo: string; sotto?: string; onChiudi: () => void; children: ReactNode
}) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onChiudi() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onChiudi])
  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={titolo}>
      <div className="dob-modal__scrim" onClick={onChiudi} />
      <div className="dob-modal__pannello" style={{ width: 'min(900px, 96vw)' }}>
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{titolo}</h3>
            {sotto && <p className="dob-modal__sotto">{sotto}</p>}
          </div>
          <div className="dob-modal__comandi"><button type="button" className="secondary" onClick={onChiudi}>{t('Chiudi')}</button></div>
        </div>
        <div className="dob-modal__corpo">{children}</div>
      </div>
    </div>
  )
}

/** Why writing is not offered here, and where to turn it on. */
export function NotaScrittura({ write }: { write: CodeProjectDetail['write'] }) {
  if (write.enabled) return null
  return (
    <p className="field__hint" style={{ margin: 0 }}>
      {write.reason === 'no_write_token'
        ? t('Per agire da qui serve il «Token di scrittura» nella connessione GitLab (scope api).')
        : t('La scrittura è spenta per questa connessione: la accende un amministratore con «Consenti la scrittura» nella connessione GitLab.')}
    </p>
  )
}

// ------------------------------------------------------------- pipelines

export function FinestraPipeline({ progetto, azione, pipelineId, onChiudi, onFatto }: {
  progetto: CodeProjectDetail
  azione: 'run' | 'retry' | 'cancel'
  pipelineId?: number
  onChiudi: () => void
  onFatto: (messaggio: string) => void
}) {
  const [refs, setRefs] = useState<{ branches: string[]; tags: string[] } | null>(null)
  const [ref, setRef] = useState(progetto.default_branch)
  const [piano, setPiano] = useState<SupplyPipelinePlan | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)

  useEffect(() => {
    if (azione === 'run') {
      api.supplyRefs(progetto.id).then((r) => setRefs(r)).catch(setErrore)
    } else {
      api.supplyPipelinePreview(progetto.id, { action: azione, pipeline_id: pipelineId }).then(setPiano).catch(setErrore)
    }
  }, [azione, pipelineId, progetto.id])

  async function prepara() {
    setErrore(null)
    try { setPiano(await api.supplyPipelinePreview(progetto.id, { action: 'run', ref })) } catch (e) { setErrore(e) }
  }

  async function esegui() {
    setLavoro(true)
    setErrore(null)
    try {
      const r = await api.supplyPipelineAction(progetto.id, { action: azione, ref: azione === 'run' ? ref : undefined, pipeline_id: pipelineId })
      onFatto(azione === 'run' ? t('Pipeline #{id} avviata su «{ref}».', { id: r.pipeline_id ?? '', ref })
        : azione === 'retry' ? t('Pipeline #{id}: job falliti fatti ripartire.', { id: pipelineId ?? '' })
          : t('Pipeline #{id} annullata.', { id: pipelineId ?? '' }))
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  const titolo = azione === 'run' ? t('Avvia una pipeline') : azione === 'retry' ? t('Riprova la pipeline #{id}', { id: pipelineId ?? '' })
    : t('Annulla la pipeline #{id}', { id: pipelineId ?? '' })
  return (
    <Finestra titolo={titolo} sotto={progetto.path} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      {azione === 'run' && !piano && (
        refs ? (
          <>
            <div className="field" style={{ maxWidth: 420 }}>
              <label htmlFor="pipeline-ref">{t('Ramo o tag')}</label>
              <select id="pipeline-ref" value={ref} onChange={(e) => setRef(e.target.value)}>
                <optgroup label={t('Rami')}>{refs.branches.map((b) => <option key={b} value={b}>{b}</option>)}</optgroup>
                {refs.tags.length > 0 && <optgroup label={t('Tag')}>{refs.tags.map((x) => <option key={x} value={x}>{x}</option>)}</optgroup>}
              </select>
              <div className="field__hint">{t('Nessuna variabile si può aggiungere da qui: una variabile scritta in console (come CI_DEBUG_TRACE) potrebbe stampare i segreti nei log dei job.')}</div>
            </div>
            <div className="toolbar"><button type="button" onClick={() => void prepara()}>{t('Prepara il piano')}</button></div>
          </>
        ) : <Spinner />
      )}
      {piano && (
        <>
          <p style={{ marginTop: 0 }}><strong>{t('Niente è ancora stato fatto.')}</strong></p>
          <ul style={{ paddingLeft: 18 }}>
            {piano.consequences.map((c) => <li key={c}>{conseguenza(c, { ref: piano.ref })}</li>)}
          </ul>
          {piano.jobs.length > 0 && (
            <ul style={{ paddingLeft: 18 }}>
              {piano.jobs.map((j, i) => <li key={i}><BadgePipeline stato={j.status} /> {j.name} <small className="text-muted">({j.stage})</small></li>)}
            </ul>
          )}
          {azione !== 'run' && piano.jobs.length === 0 && (
            <p className="text-muted">{azione === 'retry' ? t('Nessun job fallito da far ripartire.') : t('Nessun job in corso da fermare.')}</p>
          )}
          <div className="toolbar">
            {azione === 'run' && <button type="button" className="secondary" onClick={() => setPiano(null)} disabled={lavoro}>{t('Indietro')}</button>}
            <button type="button" className={azione === 'cancel' ? 'danger' : ''} onClick={() => void esegui()}
              disabled={lavoro || (azione !== 'run' && piano.jobs.length === 0)}>
              {lavoro ? t('In corso…') : azione === 'run' ? t('Avvia la pipeline') : azione === 'retry' ? t('Fai ripartire i job') : t('Annulla la pipeline')}
            </button>
          </div>
        </>
      )}
    </Finestra>
  )
}

// ------------------------------------------------------ merge requests

export type TipoModifica = 'sbom_job' | 'gate_job' | 'renovate_onboarding' | 'renovate_osv' | 'renovate_runner'

function titoloModifica(k: TipoModifica): string {
  return ({
    sbom_job: t('Aggiungi alla pipeline il job che produce l’SBOM'),
    gate_job: t('Aggiungi alla pipeline il cancello di sicurezza'),
    renovate_onboarding: t('Proponi la configurazione di Renovate'),
    renovate_osv: t('Accendi gli avvisi di sicurezza OSV di Renovate'),
    renovate_runner: t('Prepara il runner di Renovate'),
  } as Record<TipoModifica, string>)[k]
}

function introModifica(k: TipoModifica): string {
  return ({
    sbom_job: t('Il job censo-sbom genera a ogni pipeline censo-sbom.cdx.json (CycloneDX 1.6) con cdxgen e lo lascia negli artefatti, da dove CENSO lo legge da solo: nessun segreto nella pipeline.'),
    gate_job: t('Il job censo-gate chiede a CENSO, dopo censo-sbom, il verdetto sull’SBOM di questa pipeline secondo la regola del cancello, e stampa il rapporto nel log. Il token resta nella variabile CI mascherata, mai nel file.'),
    renovate_onboarding: t('Una merge request che aggiunge renovate.json: Renovate con gli avvisi di sicurezza OSV accesi, che apre merge request per correggere le librerie vulnerabili. La descrizione elenca quelle che CENSO trova nell’ultimo SBOM.'),
    renovate_osv: t('Una merge request che accende «osvVulnerabilityAlerts» nella configurazione di Renovate esistente: senza, Renovate non apre merge request per le vulnerabilità.'),
    renovate_runner: t('Un job di avvio su richiesta nel progetto che esegue Renovate: parte solo quando CENSO avvia una pipeline per un progetto preciso, e tratta quello solo. Il token di Renovate resta in quel progetto.'),
  } as Record<TipoModifica, string>)[k]
}

export function FinestraModifica({ progetto, tipo, onChiudi, onFatto }: {
  progetto: { id: string; path: string }
  tipo: TipoModifica
  onChiudi: () => void
  onFatto: (messaggio: string, url?: string) => void
}) {
  const ci = tipo === 'sbom_job' || tipo === 'gate_job' || tipo === 'renovate_runner'
  const [opzioni, setOpzioni] = useState<SupplyChangeOptions>({ install_deps: false, gitlab_report: false, tags: [],
    security_only: true, scheduled: false })
  const [etichette, setEtichette] = useState('')
  const [piano, setPiano] = useState<SupplyChangePlan | null>(null)
  const [stadi, setStadi] = useState<string[] | null>(ci ? null : [])
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)

  const corpo = (): SupplyChangeOptions => ({ ...opzioni, tags: etichette.split(',').map((x) => x.trim()).filter(Boolean) })

  // For the CI changes, a first plan with the defaults to learn stages and image.
  useEffect(() => {
    if (!ci) return
    api.supplyChangePreview(progetto.id, { kind: tipo, options: {} })
      .then((p) => { setStadi(p.stages); setOpzioni((o) => ({ ...o, stage: p.stage, image: p.image })) })
      .catch((e) => { setStadi([]); setErrore(e) })
  }, [ci, progetto.id, tipo])

  async function prepara() {
    setErrore(null)
    setLavoro(true)
    try { setPiano(await api.supplyChangePreview(progetto.id, { kind: tipo, options: corpo() })) } catch (e) { setErrore(e) } finally { setLavoro(false) }
  }

  async function apri() {
    if (!piano) return
    setErrore(null)
    setLavoro(true)
    try {
      const r = await api.supplyChangeOpen(progetto.id, { kind: tipo, options: corpo(), base_commit: piano.base_commit })
      onFatto(t('Merge request !{iid} aperta dal ramo {ramo}.', { iid: r.mr_iid ?? '', ramo: r.branch }), r.mr_url)
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <Finestra titolo={titoloModifica(tipo)} sotto={progetto.path} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      {!piano ? (
        stadi === null ? <Spinner /> : (
          <>
            <p className="field__hint" style={{ marginTop: 0 }}>{introModifica(tipo)}</p>
            {ci && stadi.length > 0 && (
              <div className="toolbar" style={{ alignItems: 'flex-start' }}>
                <div className="field" style={{ minWidth: 180 }}>
                  <label htmlFor="mod-stage">{t('Stage')}</label>
                  <select id="mod-stage" value={opzioni.stage ?? ''} onChange={(e) => setOpzioni({ ...opzioni, stage: e.target.value })}>
                    {stadi.filter((x) => x !== '.pre' && x !== '.post').map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <div className="field" style={{ flex: 1, minWidth: 260 }}>
                  <label htmlFor="mod-image">{tipo === 'sbom_job' ? t('Immagine di cdxgen') : tipo === 'gate_job' ? t('Immagine di curl') : t('Immagine di Renovate')}</label>
                  <input id="mod-image" type="text" className="mono" value={opzioni.image ?? ''} maxLength={300}
                    onChange={(e) => setOpzioni({ ...opzioni, image: e.target.value })} />
                  <div className="field__hint">{t('Fissata per versione e per impronta (@sha256:…). Con un registro interno metti qui il suo mirror.')}</div>
                </div>
                {(tipo === 'sbom_job' || tipo === 'gate_job') && (
                  <div className="field" style={{ minWidth: 200 }}>
                    <label htmlFor="mod-tags">{t('Tag dei runner')}</label>
                    <input id="mod-tags" type="text" value={etichette} placeholder={t('vuoto: qualsiasi runner')}
                      onChange={(e) => setEtichette(e.target.value)} />
                  </div>
                )}
              </div>
            )}
            {tipo === 'sbom_job' && (
              <>
                <div className="checkbox">
                  <input id="mod-deps" type="checkbox" checked={Boolean(opzioni.install_deps)}
                    onChange={(e) => setOpzioni({ ...opzioni, install_deps: e.target.checked })} />
                  <label htmlFor="mod-deps">
                    {t('Installa le dipendenze per risolverle')}
                    <small>{t('Spenta (consigliato), cdxgen legge i file di blocco così come sono. Accesa, esegue i gestori di pacchetti: servono le credenziali dei registri privati nella pipeline.')}</small>
                  </label>
                </div>
                <div className="checkbox">
                  <input id="mod-report" type="checkbox" checked={Boolean(opzioni.gitlab_report)}
                    onChange={(e) => setOpzioni({ ...opzioni, gitlab_report: e.target.checked })} />
                  <label htmlFor="mod-report">
                    {t('Dichiaralo anche come report «cyclonedx» di GitLab')}
                    <small>{t('Su GitLab Ultimate alimenta la Dependency List e le politiche di sicurezza del progetto: cambia ciò che GitLab vi mostra.')}</small>
                  </label>
                </div>
              </>
            )}
            {tipo === 'renovate_onboarding' && (
              <div className="checkbox">
                <input id="mod-sicurezza" type="checkbox" checked={opzioni.security_only !== false}
                  onChange={(e) => setOpzioni({ ...opzioni, security_only: e.target.checked })} />
                <label htmlFor="mod-sicurezza">
                  {t('Solo aggiornamenti di sicurezza (consigliato)')}
                  <small>{t('Renovate apre merge request solo per correggere le vulnerabilità. Spenta, propone anche tutti gli altri aggiornamenti delle dipendenze.')}</small>
                </label>
              </div>
            )}
            {tipo === 'renovate_runner' && (
              <div className="checkbox">
                <input id="mod-pianificato" type="checkbox" checked={Boolean(opzioni.scheduled)}
                  onChange={(e) => setOpzioni({ ...opzioni, scheduled: e.target.checked })} />
                <label htmlFor="mod-pianificato">
                  {t('Anche un giro pianificato sui gruppi della connessione')}
                  <small>{t('Un secondo job che gira quando una pianificazione di GitLab lo avvia (la pianificazione la crei tu in CI/CD → Pianificazioni) e tratta i progetti dei gruppi che hanno già una configurazione di Renovate.')}</small>
                </label>
              </div>
            )}
            <div className="toolbar"><button type="button" onClick={() => void prepara()} disabled={lavoro}>{lavoro ? t('Preparazione…') : t('Prepara il piano')}</button></div>
          </>
        )
      ) : (
        <>
          <p style={{ marginTop: 0 }}>
            <strong>{t('Niente è ancora stato scritto.')}</strong>{' '}
            {piano.new_file ? t('Si crea {f}.', { f: piano.file })
              : piano.update && ci ? t('Si aggiorna il blocco di CENSO in {f}.', { f: piano.file })
                : ci ? t('Si aggiunge un blocco in fondo a {f}: il resto del file non cambia.', { f: piano.file })
                  : t('Si modifica {f}.', { f: piano.file })}
          </p>
          {piano.open_change && (
            <div className="banner banner--warn">
              {t('C’è già una merge request di CENSO aperta per questo:')} <LinkEsterno href={piano.open_change.mr_url}>!{piano.open_change.mr_iid}</LinkEsterno>
            </div>
          )}
          <Diff testo={piano.diff} />
          {piano.lint.reason !== 'not_ci' && (
            <>
              <p style={{ margin: '10px 0 4px' }}>
                <strong>{t('CI Lint di GitLab')}:</strong>{' '}
                {piano.lint.valid === true ? <span className="badge badge--ok">{t('configurazione valida')}</span>
                  : piano.lint.valid === false ? <span className="badge badge--error">{t('errori')}</span>
                    : <span className="badge badge--muted">{t('non eseguita')}</span>}
              </p>
              {piano.lint.errors.length > 0 && <ul style={{ paddingLeft: 18, color: 'var(--error)' }}>{piano.lint.errors.map((e, i) => <li key={i}><small>{e}</small></li>)}</ul>}
              {piano.lint.warnings.length > 0 && <ul style={{ paddingLeft: 18 }}>{piano.lint.warnings.map((e, i) => <li key={i}><small>{e}</small></li>)}</ul>}
            </>
          )}
          {piano.vulnerable.length > 0 && (
            <>
              <h4 style={{ margin: '12px 0 4px' }}>{t('Le librerie vulnerabili citate nella merge request')}</h4>
              <ul style={{ paddingLeft: 18 }}>
                {piano.vulnerable.slice(0, 10).map((v) => (
                  <li key={`${v.name}@${v.version}`}>
                    <span className="mono">{v.name}@{v.version}</span>{v.fixed && <small className="text-muted"> → {v.fixed}</small>}
                    {v.exploited && <> <span className="badge badge--error">{t('sfruttata')}</span></>}
                  </li>
                ))}
                {piano.vulnerable.length > 10 && <li className="text-muted">{t('e altre {n}', { n: piano.vulnerable.length - 10 })}</li>}
              </ul>
            </>
          )}
          <h4 style={{ margin: '12px 0 4px' }}>{t('Conseguenze')}</h4>
          <ul style={{ paddingLeft: 18 }}>
            {piano.consequences.map((c) => <li key={c}>{conseguenza(c, { target: piano.target_branch })}</li>)}
          </ul>
          <div className="toolbar">
            <button type="button" className="secondary" onClick={() => setPiano(null)} disabled={lavoro}>{t('Indietro')}</button>
            <button type="button" onClick={() => void apri()} disabled={lavoro || piano.lint.valid === false || Boolean(piano.open_change)}>
              {lavoro ? t('Apertura…') : t('Apri la merge request')}
            </button>
          </div>
        </>
      )}
    </Finestra>
  )
}

// ---------------------------------------------------- Renovate, now

export function FinestraAvvioRenovate({ progetto, onChiudi, onFatto }: {
  progetto: { id: string; path: string }
  onChiudi: () => void
  onFatto: (messaggio: string, url?: string) => void
}) {
  const [piano, setPiano] = useState<RenovateTriggerPlan | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  useEffect(() => { api.supplyRenovatePreview(progetto.id).then(setPiano).catch(setErrore) }, [progetto.id])

  async function avvia() {
    setErrore(null)
    setLavoro(true)
    try {
      const r = await api.supplyRenovateRun(progetto.id)
      onFatto(t('Renovate avviato su {p}: pipeline #{id} nel runner.', { p: progetto.path, id: r.pipeline_id ?? '' }), r.web_url)
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <Finestra titolo={t('Avvia Renovate adesso')} sotto={progetto.path} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      {!piano ? (!errore && <Spinner />) : (
        <>
          <p style={{ marginTop: 0 }}><strong>{t('Niente è ancora stato fatto.')}</strong></p>
          <ul style={{ paddingLeft: 18 }}>
            <li>{t('Nel progetto del runner {r} parte una pipeline con CENSO_RENOVATE_REPOSITORY = {p}: Renovate tratta solo questo progetto.', { r: piano.runner, p: piano.project })}</li>
            <li>{t('Renovate apre (o aggiorna) le sue merge request in {p}, con il suo account e le sue regole.', { p: piano.project })}</li>
            <li>{piano.token === 'present' ? t('RENOVATE_TOKEN è impostato nel runner.')
              : piano.token === 'absent' ? t('RENOVATE_TOKEN non c’è nel runner: la pipeline fallirà finché un Maintainer non lo aggiunge.')
                : t('Non si sa se RENOVATE_TOKEN ci sia (può stare nel gruppo o nell’istanza): se manca, la pipeline fallisce.')}</li>
            <li>{t('Avviare una pipeline con una variabile richiede il ruolo Maintainer sul progetto del runner.')}</li>
          </ul>
          <div className="toolbar">
            <button type="button" onClick={() => void avvia()} disabled={lavoro}>{lavoro ? t('In corso…') : t('Avvia Renovate')}</button>
          </div>
        </>
      )}
    </Finestra>
  )
}

/** The state of a connection's Renovate runner, in words. */
export function StatoRunner({ r }: { r: RenovateRunner | null | undefined }) {
  if (!r || !r.project_id) {
    return <span className="text-muted">{r?.configured ? t('Runner di Renovate indicato ma non trovato: controlla il percorso nella connessione.') : t('Nessun runner di Renovate trovato.')}</span>
  }
  return (
    <span>
      {t('Runner di Renovate')}: <span className="mono">{r.path}</span>
      {' · '}{r.on_demand ? t('avvio su richiesta di CENSO pronto') : t('senza avvio su richiesta di CENSO')}
      {' · '}{r.token === 'present' ? t('RENOVATE_TOKEN presente') : r.token === 'absent' ? t('RENOVATE_TOKEN assente') : t('RENOVATE_TOKEN non verificabile')}
    </span>
  )
}

// ---------------------------------------------------------------- history

export function etichettaModifica(k: string): string {
  return ({
    pipeline_run: t('pipeline avviata'), pipeline_retry: t('pipeline riprovata'), pipeline_cancel: t('pipeline annullata'),
    sbom_job: t('job SBOM'), gate_job: t('cancello di sicurezza'), renovate_onboarding: t('Renovate: configurazione'),
    renovate_osv: t('Renovate: avvisi OSV'), renovate_runner: t('Renovate: runner'), renovate_trigger: t('Renovate: avvio mirato'),
  } as Record<string, string>)[k] ?? k
}

const STATI: Record<string, [string, () => string]> = {
  done: ['badge--ok', () => t('fatto')], failed: ['badge--error', () => t('non riuscito')], opened: ['badge--info', () => t('MR aperta')],
  merged: ['badge--ok', () => t('MR approvata')], closed: ['badge--muted', () => t('MR chiusa')],
}

export function TabellaModifiche({ righe, conProgetto = true }: { righe: SupplyChangeRow[]; conProgetto?: boolean }) {
  return (
    <DataTable rows={righe} rowKey={(r) => r.id} conteggio={{ nome: t('modifiche') }} initialSort={{ key: 'created_at', dir: 'desc' }}
      emptyText={t('Nessuna azione di CENSO in GitLab finora.')} columns={[
        { key: 'created_at', header: t('Quando'), type: 'date', value: (r) => r.created_at ?? '', render: (r) => <small>{quando(r.created_at)}</small> },
        ...(conProgetto ? [{ key: 'project', header: t('Progetto'), value: (r: SupplyChangeRow) => r.project }] : []),
        { key: 'kind', header: t('Cosa'), value: (r) => etichettaModifica(r.kind) },
        { key: 'status', header: t('Stato'), value: (r) => r.status, render: (r) => {
          const v = STATI[r.status]
          return <span className={`badge ${v ? v[0] : 'badge--muted'}`}>{v ? v[1]() : r.status}</span>
        } },
        { key: 'link', header: t('Dove'), value: (r) => r.mr_iid ?? r.pipeline_id ?? '', render: (r) => (
          r.mr_url ? <LinkEsterno href={r.mr_url}>{r.mr_iid ? `!${r.mr_iid}` : `#${r.pipeline_id ?? ''}`}</LinkEsterno>
            : r.pipeline_id ? <span className="mono">#{r.pipeline_id}</span> : '—'
        ) },
        { key: 'requested_by', header: t('Chiesto da'), value: (r) => r.requested_by },
        { key: 'result', header: t('Esito'), value: (r) => r.result, render: (r) => (r.result ? <small style={{ color: 'var(--error)' }}>{r.result}</small> : null) },
      ]} />
  )
}
